/**
 * Amazon Advertising REST helpers (Story 19.13, Task 2.3)
 *
 * Helper functions for the Amazon Ads API v2 endpoints used during OAuth
 * post-callback validation:
 *
 *   - GET /v2/profiles                — enumerate profiles per region
 *
 * Amazon-specific call shape:
 *   - **Lowercase `bearer ` prefix** (per amzn/ads-advanced-tools-docs
 *     Discussion #161 — uppercase `Bearer` is rejected by some regional
 *     endpoints).
 *   - **`Amazon-Advertising-API-ClientId` header REQUIRED** on every Ads API
 *     call (even discovery — separate from the Bearer Authorization header).
 *   - **`Amazon-Advertising-API-Scope` header** is NOT included in profile
 *     discovery (we don't yet have a profileId). It is required for
 *     SP/SB/SD/DSP report endpoints (Stories 19.14-19.16).
 *
 * Region-partitioning reality:
 *   - Amazon Ads profiles are partitioned across 3 regional API hosts:
 *     `advertising-api.amazon.com` (NA), `advertising-api-eu.amazon.com` (EU),
 *     `advertising-api-fe.amazon.com` (FE).
 *   - Each profile belongs to exactly one regional host. A multi-region
 *     advertiser will have profiles on multiple hosts that DO NOT cross-list.
 *   - Discovery requires sequential calls to all 3 regional `/v2/profiles`
 *     endpoints (NOT `Promise.all` — preserves LWA quota fairness).
 *   - Per-region 401/403 graceful degrades to `regionStatus: "unauthorized"`;
 *     5xx / timeout / malformed-JSON degrades to `regionStatus: "outage"`
 *     (Round 2 Pre-mortem Failure D — outage-vs-unauthorized distinction).
 *
 * Round 1 SRE budget: per-region timeout is **1500ms** (NOT default 25s) so
 * worst-case 3 × 1500ms = 4.5s stays within Cloudflare subrequest budget.
 *
 * Round 3 Red Team Vuln 1: every regional fetch uses `redirect: "manual"` to
 * prevent SSRF via a 302 to attacker-controlled hosts that would forward the
 * bearer token. A 302 from the regional host is treated as `regionStatus:
 * "outage"` rather than silently followed.
 *
 * @see https://advertising.amazon.com/API/docs/en-us/reference/2/profiles
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { AmazonAdsApiCallError, parseAmazonAdsApiError } from "./errors";
import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
  sanitizeAmazonAdsErrorMessage,
} from "./oauth";

// ============================================================================
// Constants
// ============================================================================

/**
 * Per-region discovery timeout. Round 1 SRE Cloudflare subrequest budget —
 * NOT the default `safeFetch` 25s timeout. Worst-case 3 sequential regional
 * calls × 1.5s = 4.5s; total callback latency budget (token-exchange ~2s +
 * 3 × 1.5s discovery + DB write ~0.3s) ≈ 6.8s, within Cloudflare's worker
 * subrequest envelope on lower plan tiers.
 */
export const AMAZON_ADS_REGION_DISCOVERY_TIMEOUT_MS = 1500;

// ============================================================================
// Types
// ============================================================================

export interface AmazonAdsAccountInfo {
  /** "seller" | "vendor" | "agency" */
  type: string;
  /** Internal Amazon entity id */
  id: string;
  /** Display name — sole-proprietor sellers may use personal legal name (PII) */
  name: string;
  /** Marketplace string id (e.g. "ATVPDKIKX0DER" for amazon.com) */
  marketplaceStringId: string;
  /** Whether the entity has a valid payment method configured */
  validPaymentMethod: boolean;
  /** Optional non-traditional account subtype */
  subType?: "KDP_AUTHOR" | "AMAZON_ATTRIBUTION";
}

/**
 * A profile row enriched with its discovered region. Amazon's `/v2/profiles`
 * response body does NOT include the region — we tag based on which regional
 * host returned the row.
 */
export interface AmazonAdsProfile {
  /** Numeric profile id (stored as string for consistency with other integrations) */
  profileId: string;
  countryCode: string;
  currencyCode: string;
  dailyBudget: number;
  timezone: string;
  accountInfo: AmazonAdsAccountInfo;
  /** Server-side enrichment — added by the regional fetcher */
  region: AmazonAdsRegion;
}

/**
 * Round 2 Pre-mortem Failure D — 3-branch regionStatus shape distinguishes
 * gating (expected, partial-discovery banner) from outages (transient,
 * regional-outage banner).
 */
export type RegionStatus = "ok" | "unauthorized" | "outage";

export interface RegionalProfilesResult {
  profiles: AmazonAdsProfile[];
  regionStatus: RegionStatus;
}

export interface AmazonAdsProfilesResult {
  profiles: AmazonAdsProfile[];
  /** Regions that returned at least one profile (regionStatus === "ok") */
  regionsWithProfiles: AmazonAdsRegion[];
  /** Regions that returned 401/403 (regionStatus === "unauthorized") */
  regionsWithoutAccess: AmazonAdsRegion[];
  /** Regions that returned 5xx / timeout / malformed JSON (regionStatus === "outage") */
  regionsWithOutage: AmazonAdsRegion[];
  totalProfileCount: number;
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Amazon Ads API REST headers.
 *
 * **Lowercase `bearer ` prefix** is mandatory per amzn/ads-advanced-tools-docs
 * Discussion #161 — uppercase `Bearer` is rejected by some regional endpoints.
 * Every other OAuth integration in this codebase uses uppercase `Bearer`; the
 * test name in api.test.ts explicitly says "uses lowercase bearer prefix per
 * amzn/ads-advanced-tools-docs Discussion #161" so the rationale is grep-able.
 *
 * `Amazon-Advertising-API-ClientId` is REQUIRED on every Ads API call.
 * `Amazon-Advertising-API-Scope` is required only AFTER profile selection
 * (for SP/SB/SD/DSP report endpoints in Stories 19.14-19.16); discovery-time
 * calls to /v2/profiles do NOT include this header.
 */
export function buildAmazonAdsHeaders(
  accessToken: string,
  clientId: string,
  profileId?: string
): Record<string, string> {
  if (!accessToken) {
    throw new Error("accessToken is required for Amazon Ads API calls");
  }
  if (!clientId) {
    throw new Error("clientId is required for Amazon Ads API calls");
  }
  const headers: Record<string, string> = {
    Authorization: `bearer ${accessToken}`,
    "Amazon-Advertising-API-ClientId": clientId,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (profileId !== undefined && profileId.length > 0) {
    headers["Amazon-Advertising-API-Scope"] = profileId;
  }
  return headers;
}

// ============================================================================
// Response shapes (raw Amazon envelopes)
// ============================================================================

interface RawAccountInfo {
  type?: string;
  id?: string;
  name?: string;
  marketplaceStringId?: string;
  validPaymentMethod?: boolean;
  subType?: string;
}

interface RawProfile {
  profileId?: number | string;
  countryCode?: string;
  currencyCode?: string;
  dailyBudget?: number;
  timezone?: string;
  accountInfo?: RawAccountInfo;
}

// ============================================================================
// Regional Profile Fetch
// ============================================================================

/**
 * Fetches `/v2/profiles` from a single Amazon Ads regional host.
 *
 * Returns a 3-branch typed result per Round 2 Pre-mortem Failure D:
 *   - `regionStatus: "ok"` → profiles successfully enumerated (may be empty
 *     if the user has no profiles in this region but the LWA app DOES have
 *     API access)
 *   - `regionStatus: "unauthorized"` → 401/403 (expected gating; partial-
 *     discovery banner)
 *   - `regionStatus: "outage"` → 5xx / network failure / malformed JSON /
 *     302 redirect (transient outage; different UX banner)
 *
 * Round 3 Red Team Vuln 1: `redirect: "manual"` prevents SSRF via redirect
 * injection — if Amazon ever returns a 302 the request fails with
 * `regionStatus: "outage"` rather than silently following the redirect and
 * forwarding the bearer token to an attacker-controlled host.
 *
 * Pass `timeoutMs: 1500` per Round 1 SRE budget — do NOT inherit the default
 * 25s.
 */
export async function fetchAmazonAdsProfilesForRegion(
  accessToken: string,
  clientId: string,
  region: AmazonAdsRegion
): Promise<RegionalProfilesResult> {
  const headers = buildAmazonAdsHeaders(accessToken, clientId);
  const url = `${AMAZON_ADS_REGIONAL_HOSTS[region]}/v2/profiles`;

  const result = await safeFetch(
    url,
    {
      method: "GET",
      headers,
      // Round 3 Red Team Vuln 1: never auto-follow a regional 302 — the
      // bearer token would be forwarded to the redirect target.
      redirect: "manual",
    },
    AMAZON_ADS_REGION_DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) {
    // 401/403 → unauthorized (gating, expected).
    if (result.error.code === "token_expired" || result.error.code === "permission_denied") {
      return { profiles: [], regionStatus: "unauthorized" };
    }
    // Anything else (rate_limited, provider_unavailable, network_error,
    // invalid_response from a 302/non-2xx) → outage. We deliberately group
    // 429 into "outage" here because the OAuth callback path cannot wait
    // out a rate-limit window; surfacing the partial-discovery banner is
    // more user-friendly than blocking the entire connect flow.
    return { profiles: [], regionStatus: "outage" };
  }

  // Some safeFetch variants treat 3xx as success when redirect is "manual";
  // detect non-2xx and treat as outage rather than parsing the body.
  const response = result.data;
  if (response.status >= 300) {
    return { profiles: [], regionStatus: "outage" };
  }

  let raw: RawProfile[] = [];
  try {
    const parsed = (await response.json()) as unknown;
    if (Array.isArray(parsed)) {
      raw = parsed as RawProfile[];
    } else {
      // Unexpected shape (Amazon returns an array directly per spec).
      return { profiles: [], regionStatus: "outage" };
    }
  } catch {
    // Malformed JSON — outage.
    return { profiles: [], regionStatus: "outage" };
  }

  const profiles: AmazonAdsProfile[] = [];
  for (const p of raw) {
    if (p.profileId === undefined || p.profileId === null) continue;
    // Profile IDs are numeric in the wire body but our internal storage is
    // string for consistency with other integrations. Lex-asc sort on numeric
    // strings is stable across re-auths regardless of region ordering.
    const profileIdStr = String(p.profileId);
    if (profileIdStr.length === 0) continue;

    const acct = p.accountInfo ?? {};
    const subType =
      acct.subType === "KDP_AUTHOR" || acct.subType === "AMAZON_ATTRIBUTION"
        ? acct.subType
        : undefined;

    profiles.push({
      profileId: profileIdStr,
      countryCode: typeof p.countryCode === "string" ? p.countryCode : "",
      currencyCode: typeof p.currencyCode === "string" ? p.currencyCode : "",
      dailyBudget: typeof p.dailyBudget === "number" ? p.dailyBudget : 0,
      timezone: typeof p.timezone === "string" ? p.timezone : "",
      accountInfo: {
        type: typeof acct.type === "string" ? acct.type : "",
        id: typeof acct.id === "string" ? acct.id : "",
        name: typeof acct.name === "string" ? acct.name : "",
        marketplaceStringId:
          typeof acct.marketplaceStringId === "string"
            ? acct.marketplaceStringId
            : "",
        validPaymentMethod: acct.validPaymentMethod === true,
        ...(subType !== undefined ? { subType } : {}),
      },
      region,
    });
  }

  return { profiles, regionStatus: "ok" };
}

// ============================================================================
// Multi-Region Profile Discovery
// ============================================================================

/**
 * Enumerates Amazon Ads profiles across all 3 regions (NA → EU → FE).
 *
 * **Sequential**, NOT `Promise.all` — preserves LWA quota fairness across
 * concurrent users. The LWA token endpoint quota is per `client_id`, not
 * per user; bursting 3 parallel regional fetches across many users on a
 * single LWA app could exhaust the quota and lock all users out at connect
 * time. Worst-case latency: 3 × 1.5s = 4.5s, acceptable at connect time.
 *
 * Per-region failures degrade gracefully (per AC #4):
 *   - `unauthorized` (401/403) → `regionsWithoutAccess` array, partial-
 *     discovery banner
 *   - `outage` (5xx/timeout/malformed) → `regionsWithOutage` array,
 *     regional-outage banner
 *   - `ok` with profiles → `regionsWithProfiles` array
 *
 * Total-zero-profiles → caller maps to `no-amazon-ad-profiles` (AC #4 / #12).
 * All-3-regions-401 → caller maps to `token_expired` at the adapter layer
 * (AC #4 — full-account 401 indicates token issue, not regional gating).
 *
 * Profiles in the returned flat list are sorted by **agency-priority** then
 * **lex-asc on profileId** for deterministic primary selection across re-
 * auths regardless of region ordering or Amazon's per-call ordering.
 */
export async function fetchAmazonAdsProfilesAcrossRegions(
  accessToken: string,
  clientId: string
): Promise<AmazonAdsProfilesResult> {
  const allProfiles: AmazonAdsProfile[] = [];
  const regionsWithProfiles: AmazonAdsRegion[] = [];
  const regionsWithoutAccess: AmazonAdsRegion[] = [];
  const regionsWithOutage: AmazonAdsRegion[] = [];

  // Sequential iteration — Object.entries gives deterministic order matching
  // the AMAZON_ADS_REGIONAL_HOSTS literal (NA → EU → FE).
  for (const region of Object.keys(
    AMAZON_ADS_REGIONAL_HOSTS
  ) as AmazonAdsRegion[]) {
    let regional: RegionalProfilesResult;
    try {
      regional = await fetchAmazonAdsProfilesForRegion(
        accessToken,
        clientId,
        region
      );
    } catch (err) {
      // Hard throw from the regional fetcher (e.g. malformed clientId at
      // the header-builder step) — bubble up; the orchestrator does not
      // swallow programmer errors.
      throw new AmazonAdsApiCallError(
        parseAmazonAdsApiError({
          message: err instanceof Error ? err.message : String(err),
        }) ?? {
          type: "unknown",
          message: sanitizeAmazonAdsErrorMessage(
            err instanceof Error ? err.message : String(err)
          ),
          status: null,
          retryable: false,
        },
        `Amazon Ads /v2/profiles ${region} fetch threw`
      );
    }

    if (regional.regionStatus === "ok") {
      if (regional.profiles.length > 0) {
        regionsWithProfiles.push(region);
      }
      allProfiles.push(...regional.profiles);
    } else if (regional.regionStatus === "unauthorized") {
      regionsWithoutAccess.push(region);
    } else {
      regionsWithOutage.push(region);
    }
  }

  // Sort by accountInfo.type priority (agency > seller > vendor), then
  // lex-asc on profileId. Stable across re-auths.
  const sorted = [...allProfiles].sort((a, b) => {
    const ap = typePriority(a.accountInfo.type);
    const bp = typePriority(b.accountInfo.type);
    if (ap !== bp) return ap - bp;
    return a.profileId.localeCompare(b.profileId);
  });

  return {
    profiles: sorted,
    regionsWithProfiles,
    regionsWithoutAccess,
    regionsWithOutage,
    totalProfileCount: sorted.length,
  };
}

/**
 * Type priority for primary-profile selection. Agencies typically aggregate
 * multiple sellers and are the highest-value primary; sellers are the most
 * common entity type for DTC/e-commerce; vendors are 1P-only and least
 * likely to be the primary report target.
 *
 * Unknown types (including empty) sort last so they never beat a real type.
 */
function typePriority(type: string): number {
  switch (type) {
    case "agency":
      return 0;
    case "seller":
      return 1;
    case "vendor":
      return 2;
    default:
      return 3;
  }
}

// ============================================================================
// Internal: build a typed AmazonAdsApiCallError from a safeFetch error
// ============================================================================

// ============================================================================
// Story 19.16 Task 9 / AC #9 — DSP advertiser discovery
// ============================================================================

export interface AmazonAdsDspAdvertiser {
  readonly advertiserId: string;
  readonly advertiserName: string;
  readonly currency: string;
  readonly country: string;
}

export interface AmazonAdsDspDiscoveryResult {
  readonly region: AmazonAdsRegion;
  readonly dspAccountId: string;
  readonly advertiserIds: ReadonlyArray<string>;
  readonly advertiserMetadata: ReadonlyArray<AmazonAdsDspAdvertiser>;
}

interface RawDspAdvertiser {
  advertiserId?: string | number;
  advertiserName?: string;
  currency?: string;
  country?: string;
}

/**
 * Story 19.16 Task 9.2 / AC #9 — discovers DSP advertiser IDs under an
 * agency-typed profile.
 *
 * Calls `GET /dsp/advertisers?accountId={accountInfo.id}` against the regional
 * host. Returns an empty result with `advertiserIds: []` if the endpoint
 * returns 4xx (DSP not enabled / per-region gating) — caller decides whether
 * to surface as `dsp_not_enabled` or treat as a transient outage.
 *
 * Sequential — preserves LWA quota fairness per 19.13 carry-forward.
 *
 * Privacy: `advertiserName`, `currency`, `country` ARE customer-side PII and
 * are returned for persistence in `account_metadata.dspAdvertisers.advertiserMetadata`
 * (Round 1 Privacy Officer carry-forward — never logged in raw form, but
 * persisted under the integration row's lifecycle, deleted on integration
 * delete via foreign-key cascade).
 */
export async function discoverDspAdvertisersForProfile(
  accessToken: string,
  clientId: string,
  profile: AmazonAdsProfile
): Promise<AmazonAdsDspDiscoveryResult | null> {
  if (profile.accountInfo.type !== "agency") {
    // Only agency-typed profiles have DSP entitlement per Discussion #15.
    return null;
  }
  const dspAccountId = profile.accountInfo.id;
  if (!dspAccountId) return null;

  const headers = buildAmazonAdsHeaders(accessToken, clientId, dspAccountId);
  const url = `${AMAZON_ADS_REGIONAL_HOSTS[profile.region]}/dsp/advertisers?accountId=${encodeURIComponent(dspAccountId)}`;

  const result = await safeFetch(
    url,
    {
      method: "GET",
      headers,
      redirect: "manual",
    },
    AMAZON_ADS_REGION_DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) {
    // 4xx / 5xx / network — treat as no DSP entitlement. Caller decides
    // whether to retry or surface as `dsp_not_enabled`.
    return null;
  }
  const response = result.data;
  if (response.status >= 300) return null;

  let raw: { advertisers?: RawDspAdvertiser[] };
  try {
    raw = (await response.json()) as { advertisers?: RawDspAdvertiser[] };
  } catch {
    return null;
  }

  const list = Array.isArray(raw.advertisers) ? raw.advertisers : [];
  const advertiserMetadata: AmazonAdsDspAdvertiser[] = [];
  const advertiserIds: string[] = [];

  for (const a of list) {
    const idStr = a.advertiserId !== undefined ? String(a.advertiserId) : "";
    if (!idStr) continue;
    advertiserIds.push(idStr);
    advertiserMetadata.push({
      advertiserId: idStr,
      advertiserName:
        typeof a.advertiserName === "string" ? a.advertiserName : "",
      currency: typeof a.currency === "string" ? a.currency : "",
      country: typeof a.country === "string" ? a.country : "",
    });
  }

  return {
    region: profile.region,
    dspAccountId,
    advertiserIds,
    advertiserMetadata,
  };
}

/** @internal exported for direct unit testing only */
export function buildAmazonAdsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): AmazonAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON — fall back to status-only mapping
    }
  }
  const parsed = parseAmazonAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new AmazonAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}
